import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Table, Pagination, Image, Input, Select, Switch, InputNumber, Tag, Tooltip, message } from 'antd';
import { EyeOutlined, PlusOutlined, CheckOutlined } from '@ant-design/icons';
import ModalItem from './ModalItem';
import ButtonItem from './ButtonItem';
import FiltroCategoriasInsumosItem from './FiltroCategoriasInsumosItem';
import DetalleInsumoModal from './DetalleInsumoModal';
import dineroDisabledMask from './dineroDisabledMask';

// Busqueda avanzada de insumos para los renglones de Ventas/Pedidos/Cotizaciones/Embarques
// (boton junto a "Agregar Insumo"). Lista con foto, marca, familia, precio de la lista del
// documento y existencia en su proyecto; se pueden agregar varios sin cerrar.
//
// Contrato: `onAgregar({ insumo, cantidad })` devuelve una promesa y el modal la ESPERA antes de
// dejar agregar otro. onChangeInsumoProyecto de las paginas lee el arreglo `insumos` completo,
// espera la existencia y lo reescribe completo: dos altas al mismo tiempo se pisarian.
// `insumo` es el documento que devuelve insumos/selectInsumosItemVentaFuncion para ese _id, el
// mismo que entrega SelectInsumosItem.onChange(value, insumo); por eso se vuelve a pedir aqui
// (busquedaAvanzadaInsumosVenta solo trae datos para desplegar).
//
// clienteId/usarPrecioCliente/precioId/proyectoId son una foto del documento tomada al abrir;
// se mandan igual a los dos endpoints que el select del renglon de esa pantalla.
const IMAGEN_RESPALDO = '/Resources/logoBiwoInsumos.png';
const TAMANO_PAGINA = 20;
const ESPERA_BUSQUEDA_MS = 400;
const ANCHO_MINIMO_INSUMO = 320;
const FILTROS_INICIALES = {
  texto: '',
  familiaId: undefined,
  marcaId: undefined,
  categorias: [],
  soloConExistencia: false,
};

const formatearCantidad = (valor) =>
  Number(valor || 0).toLocaleString('es-MX', { maximumFractionDigits: 4 });

const valorOGuion = (valor) => (valor || valor === 0 ? valor : <span style={{ color: '#9ca3af' }}>—</span>);

const BusquedaAvanzadaInsumosModal = ({
  visible,
  onClose,
  empresaId,
  headersToken,
  clienteId,
  usarPrecioCliente,
  precioId,
  proyectoId,
  onAgregar,
}) => {
  const [texto, setTexto] = useState('');
  const [filtros, setFiltros] = useState(FILTROS_INICIALES);
  const [pagina, setPagina] = useState(1);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalExcedido, setTotalExcedido] = useState(false);
  const [familias, setFamilias] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cantidades, setCantidades] = useState({});
  const [agregados, setAgregados] = useState({});
  const [agregandoId, setAgregandoId] = useState(null);
  const [detalle, setDetalle] = useState(null);

  const inputRef = useRef(null);
  const consultaRef = useRef(0);
  const agregandoRef = useRef(false);
  const catalogosCargadosRef = useRef(false);

  // Se limpia al cerrar (no al abrir) para que la primera consulta ya salga con los filtros
  // vacios y no se haga una de mas con los de la vez anterior.
  useEffect(() => {
    if (!visible) {
      consultaRef.current += 1;
      setTexto('');
      setFiltros(FILTROS_INICIALES);
      setPagina(1);
      setItems([]);
      setTotal(0);
      setTotalExcedido(false);
      setCantidades({});
      setAgregados({});
      setDetalle(null);
      setLoading(false);
    }
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    consultar(filtros, 1, true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, filtros]);

  useEffect(() => {
    if (!visible) return undefined;
    const limpio = texto.trim();
    const temporizador = setTimeout(() => {
      setFiltros((prev) => (prev.texto === limpio ? prev : { ...prev, texto: limpio }));
    }, ESPERA_BUSQUEDA_MS);
    return () => clearTimeout(temporizador);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  const consultar = async (filtrosActuales, paginaNueva, contar) => {
    const idConsulta = ++consultaRef.current;
    setLoading(true);
    try {
      const res = await axios.post(
        'insumos/busquedaAvanzadaInsumosVenta',
        {
          empresaId,
          query: filtrosActuales.texto,
          page: paginaNueva,
          pageSize: TAMANO_PAGINA,
          clienteId: clienteId || null,
          usarPrecioCliente,
          precioId,
          proyectoId,
          filtrosCategorias: filtrosActuales.categorias.map((f) => f.valorId),
          familiaId: filtrosActuales.familiaId || null,
          marcaId: filtrosActuales.marcaId || null,
          soloConExistencia: filtrosActuales.soloConExistencia,
          incluirCatalogos: !catalogosCargadosRef.current,
          contar,
        },
        { headers: headersToken }
      );
      // Una respuesta lenta de una busqueda anterior no debe pisar la actual.
      if (idConsulta !== consultaRef.current) return;
      if (res.data.success === 2) {
        setItems(res.data.items || []);
        setPagina(paginaNueva);
        if (contar) {
          setTotal(res.data.total || 0);
          setTotalExcedido(!!res.data.totalExcedido);
        }
        if (res.data.familias || res.data.marcas) {
          setFamilias(res.data.familias || []);
          setMarcas(res.data.marcas || []);
          catalogosCargadosRef.current = true;
        }
      } else {
        message.warning(res.data.message || 'No se pudieron buscar los insumos');
      }
    } catch {
      if (idConsulta === consultaRef.current) message.warning('No se pudieron buscar los insumos');
    } finally {
      if (idConsulta === consultaRef.current) setLoading(false);
    }
  };

  const cambiarFiltro = (campo, valor) => setFiltros((prev) => ({ ...prev, [campo]: valor }));

  const agregar = async (fila) => {
    if (agregandoRef.current) return;
    const id = String(fila._id);
    const cantidad = cantidades[id] ?? 1;
    if (!(Number(cantidad) > 0)) {
      message.info('Indique una cantidad mayor a 0');
      return;
    }
    agregandoRef.current = true;
    setAgregandoId(id);
    try {
      const res = await axios.post(
        'insumos/selectInsumosItemVentaFuncion',
        { empresaId, query: id, clienteId: clienteId || null, usarPrecioCliente },
        { headers: headersToken }
      );
      const insumo =
        res.data?.success === 2 ? (res.data.insumos || []).find((i) => String(i._id) === id) : null;
      if (!insumo) {
        message.warning('No se pudo cargar el insumo');
        return;
      }
      await onAgregar({ insumo, cantidad: Number(cantidad) });
      setAgregados((prev) => ({ ...prev, [id]: (prev[id] || 0) + 1 }));
    } catch (error) {
      console.error('Error al agregar insumo desde la busqueda avanzada:', error);
      message.warning('No se pudo agregar el insumo');
    } finally {
      agregandoRef.current = false;
      setAgregandoId(null);
      // El select del renglon nuevo trae autoFocus y se roba el foco detras del modal.
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  const cerrar = () => {
    if (agregandoRef.current) return;
    onClose();
  };

  // Insumo es la unica columna sin ancho: se queda con lo que sobre, y ANCHO_MINIMO_INSUMO es lo
  // que se le reserva en scroll.x. Si la suma de los anchos fijos alcanza a scroll.x, la tabla
  // (layout fixed por el scroll.y) la deja en 0 y la descripcion se parte letra por letra.
  const columnas = [
    {
      title: 'Foto',
      key: 'foto',
      width: 76,
      render: (_, fila) => (
        <div
          style={{
            width: 56,
            height: 56,
            border: '1px solid #f0f0f0',
            borderRadius: 6,
            background: '#fafafa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
          }}
        >
          {fila.imagen ? (
            <Image
              src={fila.imagen}
              width={54}
              height={54}
              fallback={IMAGEN_RESPALDO}
              style={{ objectFit: 'contain' }}
            />
          ) : (
            <img
              src={IMAGEN_RESPALDO}
              alt="Sin imagen"
              style={{ width: 34, height: 34, objectFit: 'contain', opacity: 0.35 }}
            />
          )}
        </div>
      ),
    },
    {
      title: 'Insumo',
      key: 'insumo',
      render: (_, fila) => {
        const datos = [
          fila.clave && `Clave: ${fila.clave}`,
          (fila.codigo || fila.codigo === 0) && `Código: ${fila.codigo}`,
          fila.codigoBarras && `C. barras: ${fila.codigoBarras}`,
        ].filter(Boolean);
        return (
          <div style={{ lineHeight: 1.35, overflowWrap: 'anywhere' }}>
            <div style={{ fontWeight: 600, color: '#111827' }}>{fila.descripcion}</div>
            {fila.nombreCliente && (
              <div style={{ fontSize: 11, color: '#008FA0' }}>Cliente: {fila.nombreCliente}</div>
            )}
            {datos.length > 0 && (
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>{datos.join('  ·  ')}</div>
            )}
            {fila.esEquivalencia && (
              <Tag color="purple" style={{ marginTop: 4, fontSize: 11 }}>
                Equivalencia: 1 {fila.unidad || ''} = {fila.factorEquivalencia} {fila.unidadPrincipal || ''}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: 'Unidad',
      key: 'unidad',
      width: 100,
      render: (_, fila) => <span style={{ overflowWrap: 'anywhere' }}>{valorOGuion(fila.unidad)}</span>,
    },
    {
      title: 'Marca / Familia',
      key: 'marcaFamilia',
      width: 160,
      render: (_, fila) => (
        <div style={{ lineHeight: 1.35, overflowWrap: 'anywhere' }}>
          <div>{fila.marca || <span style={{ color: '#9ca3af' }}>Sin marca</span>}</div>
          {fila.familia && <div style={{ fontSize: 11, color: '#6b7280' }}>{fila.familia}</div>}
        </div>
      ),
    },
    {
      title: 'Precio',
      key: 'precio',
      width: 115,
      align: 'right',
      render: (_, fila) =>
        fila.precio === null || fila.precio === undefined ? (
          <span style={{ color: '#9ca3af' }}>Sin precio</span>
        ) : (
          <div>
            <div>{dineroDisabledMask(fila.precio)}</div>
            {fila.precioDeCliente && (
              <Tag color="cyan" style={{ margin: 0 }}>
                Precio cliente
              </Tag>
            )}
          </div>
        ),
    },
    {
      title: 'Existencia',
      key: 'existencia',
      width: 105,
      align: 'right',
      render: (_, fila) => {
        if (fila.esKit) return <Tag color="geekblue" style={{ margin: 0 }}>Kit</Tag>;
        if (!fila.inventariable) return <span style={{ color: '#9ca3af', fontSize: 11 }}>No inventariable</span>;
        return (
          <span style={{ fontWeight: 600, color: fila.existencia > 0 ? '#389e0d' : '#cf1322' }}>
            {formatearCantidad(fila.existencia)}
          </span>
        );
      },
    },
    {
      title: 'Cantidad',
      key: 'cantidad',
      width: 95,
      render: (_, fila) => {
        const id = String(fila._id);
        return (
          <InputNumber
            size="small"
            min={0}
            style={{ width: '100%' }}
            value={cantidades[id] ?? 1}
            onChange={(valor) => setCantidades((prev) => ({ ...prev, [id]: valor }))}
            onPressEnter={() => agregar(fila)}
          />
        );
      },
    },
    {
      title: '',
      key: 'acciones',
      width: 175,
      render: (_, fila) => {
        const id = String(fila._id);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Tooltip title="Ver detalle e imágenes">
              <ButtonItem
                blanco
                size="small"
                lessTime
                icon={<EyeOutlined />}
                onClick={() => setDetalle({ id, nombre: fila.descripcion })}
              />
            </Tooltip>
            <ButtonItem
              type="primary"
              size="small"
              lessTime
              icon={<PlusOutlined />}
              loading={agregandoId === id}
              disabled={!!agregandoId && agregandoId !== id}
              onClick={() => agregar(fila)}
            >
              Agregar
            </ButtonItem>
            {agregados[id] ? (
              <Tooltip title="Veces agregado">
                <Tag color="green" icon={<CheckOutlined />} style={{ margin: 0 }}>
                  {agregados[id]}
                </Tag>
              </Tooltip>
            ) : null}
          </div>
        );
      },
    },
  ];
  const anchoTabla =
    columnas.reduce((suma, columna) => suma + (columna.width || 0), 0) + ANCHO_MINIMO_INSUMO;

  const filtroSelect = (props) => ({
    allowClear: true,
    showSearch: true,
    optionFilterProp: 'children',
    style: { width: 200 },
    ...props,
  });

  return (
    <>
      <ModalItem
        title="Búsqueda avanzada de insumos"
        visible={visible}
        onCancel={cerrar}
        footer={null}
        width={window.innerWidth > 1340 ? 1280 : '95%'}
        maskClosable={false}
        destroyOnClose
        paddingTop
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Input.Search
            ref={inputRef}
            autoFocus
            allowClear
            placeholder="Buscar por descripción, clave, código o código de barras"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            onSearch={(valor) => {
              const limpio = (valor || '').trim();
              setFiltros((prev) => (prev.texto === limpio ? prev : { ...prev, texto: limpio }));
            }}
            style={{ flex: '1 1 280px', minWidth: 220 }}
          />
          <Select
            {...filtroSelect({ placeholder: 'Familia' })}
            value={filtros.familiaId}
            onChange={(valor) => cambiarFiltro('familiaId', valor)}
          >
            {familias.map((f) => (
              <Select.Option key={f._id} value={f._id}>
                {f.nombre}
              </Select.Option>
            ))}
          </Select>
          <Select
            {...filtroSelect({ placeholder: 'Marca' })}
            value={filtros.marcaId}
            onChange={(valor) => cambiarFiltro('marcaId', valor)}
          >
            {marcas.map((m) => (
              <Select.Option key={m._id} value={m._id}>
                {m.nombre}
              </Select.Option>
            ))}
          </Select>
          <FiltroCategoriasInsumosItem
            empresaId={empresaId}
            headersToken={headersToken}
            filtros={filtros.categorias}
            onChange={(nuevos) => cambiarFiltro('categorias', nuevos)}
          />
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}>
            <Switch
              size="small"
              checked={filtros.soloConExistencia}
              onChange={(valor) => cambiarFiltro('soloConExistencia', valor)}
            />
            Solo con existencia
          </span>
        </div>

        <Table
          rowKey={(fila) => String(fila._id)}
          size="small"
          columns={columnas}
          dataSource={items}
          loading={loading}
          pagination={false}
          scroll={items.length ? { x: anchoTabla, y: '55vh' } : undefined}
          locale={{ emptyText: loading ? ' ' : 'No se encontraron insumos' }}
        />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 8,
            margin: '10px 0 12px 0',
          }}
        >
          <span style={{ fontSize: 12, color: '#6b7280' }}>
            {totalExcedido
              ? `Más de ${total} insumos: afine la búsqueda`
              : `${total} insumo${total === 1 ? '' : 's'}`}
          </span>
          <Pagination
            size="small"
            current={pagina}
            pageSize={TAMANO_PAGINA}
            total={total}
            showSizeChanger={false}
            onChange={(paginaNueva) => consultar(filtros, paginaNueva, false)}
          />
        </div>
      </ModalItem>

      <DetalleInsumoModal
        visible={!!detalle}
        onClose={() => setDetalle(null)}
        insumoId={detalle?.id}
        nombreInsumo={detalle?.nombre}
        headersToken={headersToken}
        empresaId={empresaId}
      />
    </>
  );
};

export default BusquedaAvanzadaInsumosModal;
